import {
  Message,
  MessageType,
} from '../loader-messaging/loader-messaging.service';
import { Injectable } from '@angular/core';
import { QueryParams } from '../query-params/query-params.class';

@Injectable({
  providedIn: 'root',
})
export class IframeService {
  prefix = '[ZaverCheckout]';

  private channel: BroadcastChannel;
  private boardcastSupported: boolean = false;
  private waitForChildrenResponse: ReturnType<typeof setTimeout> | null = null;
  private childThatAsked: boolean;
  private isAChild: boolean;
  private WAIT_FOR_CHILD_RESPONSE: number = 3000; // 3 seconds

  constructor() {}

  /////
  // Broadcast part
  /////

  initIframeApp(params: QueryParams) {
    this.isAChild = params?.child === 'true';
    if (this.isAChild) {
      this.createChannel(params);
    }
  }

  public createChannel(params: QueryParams): boolean {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      this.channel = new BroadcastChannel(this.prefix + params.id);
      this.channel.onmessage = (event) => this.handleMessage(event);
      this.boardcastSupported = true;
      console.log('channel is open ' + this.prefix + params.id);
    } else {
      this.boardcastSupported = false;
      console.warn("This browser doesn't support BroadcastChannel.");
      return false;
    }
    return true;
  }

  public closeChannel() {
    this.channel.close;
  }

  private postToChannel(message: Message) {
    this.channel.postMessage(message);
  }

  private handleMessage(event: MessageEvent) {
    const message = event.data;
    switch (message.zcoType) {
      case BroadCastMessage.SUCCESS: {
        this.receivingBrodcastSuccess(message.value);
      }
      case BroadCastMessage.CONFIRM_SUCCESS: {
        this.receivingBrodcastConfirmSuccess(message.value);
      }
    }
  }

  public brodcastSuccess(url: string) {
    if (!this.boardcastSupported) {
      window.location.href = url;
      return;
    }
    if (this.isThisInNewTab()) {
      return this.messageSuccess(url);
    }
    if (this.isAChild) {
      this.postToChannel({
        zcoType: BroadCastMessage.SUCCESS,
        value: url,
      });
      //If we don't have any children open that can postmessage, we will change on the tab.
      this.childThatAsked = true;
      this.waitForChildrenResponse = setTimeout(() => {
        window.location.href = url;
      }, this.WAIT_FOR_CHILD_RESPONSE);
    }
    window.location.href = url;
  }

  private receivingBrodcastConfirmSuccess(url) {
    if (this.childThatAsked) {
      clearTimeout(this.waitForChildrenResponse);
      window.location.href = url;
    }
  }

  private receivingBrodcastSuccess(url: string) {
    if (this.isThisInNewTab()) {
      // could get the url from parent and open a new iframe page
      this.messageSuccess(url);
      this.postToChannel({
        zcoType: BroadCastMessage.CONFIRM_SUCCESS,
        value: url,
      });
    }
  }

  /////
  // Broadcast part END
  /////

  /////
  // Postmessage to parent part
  /////

  public isThisInIframe(): boolean {
    return window.self !== window.top && window.opener === null;
  }

  public isThisAChildWindow(): boolean {
    return this.isAChild;
  }

  messageDestroyed() {
    this.postToParent({
      zcoType: MessageType.CLOSE_TAB,
      value: 'lost-connection',
    });
  }

  public messageSuccess(url) {
    this.postToParent({
      zcoType: MessageType.SUCCESS_REDIRECT,
      value: url,
      innerURL:
        window.location.href.search('&redirectOrdered=true') > -1
          ? window.location.href
          : window.location.href + '&redirectOrdered=true',
    });
  }

  messageUpdate(url: string) {
    this.postToParent({
      zcoType: MessageType.URL_UPDATE,
      value: url,
    });
  }

  // Message is consumed by loader-messaging-service in this repo.
  private postToParent(message: Message) {
    if (this.isThisInNewTab()) {
      window.opener.postMessage(this.prefix + JSON.stringify(message), '*');
    }
  }

  /////
  // Postmessage to parent part END
  /////

  public openZaverPopup(url: string): Window {
    const width: number = 400;
    const height: number = 700;
    const left: number = (window.screen.width - width) / 2;
    const top: number = (window.screen.height - height) / 2;
    let newUrl = url;
    if (!this.isThisAChildWindow()) {
      newUrl += '&child=true';
    }

    const popup: Window = window.open(
      newUrl,
      'name',
      `width=${width},height=${height},left=${left},top=${top}`
    );
    return popup;
  }

  public openZaverTab(url: string, isMobile?: boolean | null): Window {
    let newUrl = url;
    if (isMobile && this.isChrome() && !url.includes('autoStart')) {
      newUrl = url + '&autoStart=false';
    }
    if (!this.isThisAChildWindow()) {
      newUrl += '&child=true';
    }
    const tab = window.open(newUrl, '_blank');
    return tab;
  }

  setWindowMode(params: QueryParams): void {
    const id = params.id;
    let windowMode = params.windowMode
      ? params.windowMode
      : localStorage.getItem(`${id}-windowMode`);

    if (this.isWindowEnum(windowMode)) {
      localStorage.setItem(`${id}-windowMode`, windowMode);
    } else {
      localStorage.setItem(`${id}-windowMode`, WindowMode.DEFUALT);
    }
  }

  getWindowMode(id: string): string {
    if (this.isWindowEnum(localStorage.getItem(`${id}-windowMode`))) {
      return localStorage.getItem(`${id}-windowMode`);
    } else {
      return WindowMode.DEFUALT;
    }
  }

  isWindowEnum(value: string): value is WindowMode {
    return Object.values(WindowMode).includes(value as WindowMode);
  }

  isChrome(): boolean {
    const typeWindow: any = window;
    return !!typeWindow.chrome || /CriOS/.test(navigator.userAgent);
  }

  public isThisInNewTab(): boolean {
    return window.opener !== null;
  }
}

export enum WindowMode {
  DEFUALT = 'default', // iframe is default mode
  POPUP = 'popup',
  TAB = 'tab',
}

export enum BroadCastMessage {
  CLOSE_CHILDREN = 'zco-channel-close-children',
  SUCCESS = 'zco-channel-success',
  CONFIRM_SUCCESS = 'zco-channel-confirm-success',
}
